import React, { useEffect, useState } from "react";

import { ErrorIcon } from "../../../assets/icons/errorIcon";
import { LoaderWithText } from "../../../shared/Loader";

type TErrorRendererProps = {
	summary: string;
	blobLink?: string | null;
};

const ErrorRenderer: React.FC<TErrorRendererProps> = ({
	summary,
	blobLink,
}) => {
	const [fullError, setFullError] = useState<string>(summary);
	const [loading, setLoading] = useState<boolean>(false);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [expanded, setExpanded] = useState<boolean>(false);

	useEffect(() => {
		const fetchFullError = async () => {
			if (!blobLink) {
				// No blob link, use summary as full error
				setFullError(summary);
				return;
			}

			// Only fetch when expanded
			if (!expanded) {
				return;
			}

			setLoading(true);
			setFetchError(null);
			try {
				const response = await fetch(blobLink);
				if (!response.ok) {
					throw new Error("Failed to fetch error details");
				}
				const text = await response.text();
				setFullError(text);
			} catch (err) {
				console.error("Error fetching full error content:", err);
				setFetchError("Failed to load full error details");
				setFullError(summary); // Fallback to summary
			} finally {
				setLoading(false);
			}
		};

		void fetchFullError();
	}, [blobLink, expanded, summary]);

	const hasLargeError = !!blobLink;

	return (
		<div className="code-execution-error">
			<div className="error-header">
				<svg
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
						stroke="#F97316"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
				<span className="error-title">Code Execution Error</span>
			</div>
			<div className="error-content">
				{!expanded && <div>{summary}</div>}
				{expanded && loading && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							padding: "20px",
						}}
					>
						<LoaderWithText text="Loading error details..." />
					</div>
				)}
				{expanded && !loading && fetchError && (
					<div className="blob-data-error">
						<ErrorIcon />
						<span>{fetchError}</span>
					</div>
				)}
				{expanded && !loading && !fetchError && (
					<pre
						style={{
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
							margin: 0,
						}}
					>
						{fullError}
					</pre>
				)}
				{hasLargeError && (
					<button
						onClick={() => setExpanded(!expanded)}
						style={{
							marginTop: "8px",
							padding: "4px 8px",
							fontSize: "12px",
							cursor: "pointer",
							background: "#f97316",
							color: "white",
							border: "none",
							borderRadius: "4px",
						}}
					>
						{expanded ? "Show less" : "Show full error"}
					</button>
				)}
			</div>
		</div>
	);
};

export default ErrorRenderer;
